// GENERATED from the registered Life Plan Course Source (revision 8, 20 modules, 131 questions).
// Do not edit by hand: the learner-facing structure is fixed, so the Life Plan theme hard-codes against these keys.
// Regenerate with wisefriend-qa/lifeplan-themes/tools/gen-catalog.mjs when the course changes.

export type LifePlanPartId = 'orientation' | 'now' | 'vision' | 'path';
export type LifePlanQuestionType =
  | 'description' | 'multiple_choice' | 'multiple_select' | 'multiple_reveal' | 'text_or_audio' | 'text_list'
  | 'rating' | 'boolean' | 'horizontal_drag_drop' | 'callback_summary';

export interface LifePlanPart {
  id: LifePlanPartId;
  /** 0 = Orientation, 1 = Part I (Now), 2 = Part II (Vision), 3 = Part III (Next steps). */
  order: 0 | 1 | 2 | 3;
  numeral: '' | 'I' | 'II' | 'III';
  /** Learner-facing question for the part ("Where am I now?"). */
  title: string;
  /** One-word waypoint used on route lines. */
  waypoint: string;
  /** Chapter key/title as stored in the course (Orientation has no chapter). */
  chapterKey: string | null;
  chapterTitle: string | null;
}

export interface LifePlanOption {
  /** The option as the card shows it (plain text). */
  text: string;
  /** Tap-to-reveal back of the card. */
  detail?: string;
  /** Drag-and-drop column the item belongs in. */
  bucket?: 'left' | 'right';
  /** The "Other" write-in option. */
  other?: true;
}

export interface LifePlanQuestion {
  key: string;
  type: LifePlanQuestionType;
  /** Plain printed text: Markdown marks removed, answer references shown as their fallback wording. */
  header: string;
  text: string;
  subtext?: string;
  /** Rating, boolean or sorting labels. */
  low?: string;
  high?: string;
  options?: readonly LifePlanOption[];
  listLength?: number;
  /** Text-list line labels ("Thought:", "Evidence:", ...). */
  labels?: readonly string[];
  maxSelections?: number;
  /** "The Science" cards: the explanation and the correct answer. */
  explanation?: string;
  correct?: boolean;
}

export interface LifePlanModule { number: number; slug: string; title: string; part: LifePlanPartId; questions: readonly LifePlanQuestion[] }

export const LIFE_PLAN_PARTS: readonly LifePlanPart[] = [{"id":"orientation","order":0,"numeral":"","title":"Orientation","waypoint":"Orientation","chapterKey":null,"chapterTitle":null},{"id":"now","order":1,"numeral":"I","title":"Where am I now?","waypoint":"Now","chapterKey":"where-am-i-now","chapterTitle":"I. Where am I now?"},{"id":"vision","order":2,"numeral":"II","title":"Where do I want to go?","waypoint":"Vision","chapterKey":"where-do-i-want-to-go","chapterTitle":"II. Where do I want to go?"},{"id":"path","order":3,"numeral":"III","title":"How do I get there?","waypoint":"Next steps","chapterKey":"how-can-i-get-there","chapterTitle":"III. How can I get there?"}];

/** Quote cards: question key to the quoted author (the card header). */
export const LIFE_PLAN_QUOTE_AUTHORS: Readonly<Record<string, string>> = {"lp_0002":"Rumi","lp_0101":"Aristotle","lp_0186":"Simone Weil","lp_0201":"Kenichi Ohmae","success_quote":"John Wooden","lp_0213":"Ralph Waldo Emerson","lp_0301":"Epictetus","lp_0340":"Epictetus","lp_0342":"Jim Rohn","lp_0375":"Aristotle","lp_0376":"James Clear","lp_0403":"Jane Goodall"};

export const LIFE_PLAN_MODULES: readonly LifePlanModule[] = [
 {
  "number": 1,
  "slug": "orientation",
  "title": "Orientation",
  "part": "orientation",
  "questions": [
   {
    "key": "lp_0002",
    "type": "description",
    "header": "Rumi",
    "text": "“Yesterday I was clever, so I wanted to change the world. Today I am wise, so I am changing myself.”"
   },
   {
    "key": "lp_0001",
    "type": "description",
    "header": "Welcome",
    "text": "This is your Life Plan. A space to look honestly at where you are, name where you want to go, and choose how to get there.\n\nGo at your own pace. Nothing here is graded."
   },
   {
    "key": "the_promise",
    "type": "description",
    "header": "The Promise",
    "text": "If you let go of negative thoughts and feelings, give your best, and follow what you know is right, magic will happen."
   },
   {
    "key": "priv_reminder",
    "type": "description",
    "header": "Reminder",
    "text": "Anything you write here stays private and anonymous, unless you choose to share it.\n\nYour individual answers are not shared outside Wise Friend.",
    "subtext": "This is your space to be honest."
   },
   {
    "key": "lp_0003",
    "type": "multiple_choice",
    "header": "",
    "text": "Before we begin, what do you most want from this process right now?",
    "subtext": "Pick what fits today.",
    "options": [
     {
      "text": "Clarity"
     },
     {
      "text": "Direction"
     },
     {
      "text": "Relief"
     },
     {
      "text": "Motivation"
     },
     {
      "text": "A fresh start"
     },
     {
      "text": "Not sure yet"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "orient_prior",
    "type": "multiple_choice",
    "header": "",
    "text": "Have you ever made a plan for your life the way you would for a goal or a business?",
    "subtext": "Quick check.",
    "options": [
     {
      "text": "Never"
     },
     {
      "text": "Once or twice"
     },
     {
      "text": "I reflect often"
     },
     {
      "text": "Not formally"
     }
    ]
   },
   {
    "key": "lp_0004",
    "type": "multiple_reveal",
    "header": "",
    "text": "There are a handful of mindsets that support your journey, and a handful that don't. Let's explore a few.",
    "subtext": "Tap each to explore.",
    "options": [
     {
      "text": "Curious vs Closed Off",
      "detail": "Curious: growth is everywhere.\n\nClosed off: I already know this, let me speed through."
     },
     {
      "text": "Present vs Distracted",
      "detail": "Present: letting go of distractions, again and again.\n\nDistracted: protecting my ego, chasing distractions."
     },
     {
      "text": "Process-Oriented vs Self-Conscious",
      "detail": "Process: humbly doing the work.\n\nSelf-conscious: writing what I should say in case anyone sees."
     },
     {
      "text": "No Quit vs Halfway",
      "detail": "No quit: showing up tomorrow, and the day after.\n\nHalfway: no follow through, since I can give up."
     }
    ]
   },
   {
    "key": "lp_0005",
    "type": "multiple_select",
    "header": "",
    "text": "Which mindsets do you want to bring into this process?",
    "subtext": "Select the ones you want to lead with.",
    "options": [
     {
      "text": "Curious"
     },
     {
      "text": "Present"
     },
     {
      "text": "Process-oriented"
     },
     {
      "text": "No quit"
     }
    ]
   },
   {
    "key": "mindset_oneliner",
    "type": "text_or_audio",
    "header": "You get out what you put in",
    "text": "What one-liner do you want to write to yourself, to remember the attitude you want to bring?",
    "subtext": "A short phrase you can return to. For example: show up curious, keep it honest."
   }
  ]
 },
 {
  "number": 2,
  "slug": "fuel",
  "title": "Fuel",
  "part": "now",
  "questions": [
   {
    "key": "lp_0101",
    "type": "description",
    "header": "Aristotle",
    "text": "“Knowing yourself is the beginning of all wisdom.”"
   },
   {
    "key": "lp_0102",
    "type": "description",
    "header": "Where am I now?",
    "text": "This first section is a self-inventory. We start by noticing what is already here, before we try to change anything."
   },
   {
    "key": "lp_0103",
    "type": "text_list",
    "header": "Highlights",
    "text": "What are some of the most meaningful highlights of your last 12 months?\n\nThen zoom out. What are the meaningful highlights of your life?",
    "subtext": "Add five highlights. Small, everyday moments count, too.",
    "listLength": 5
   },
   {
    "key": "lp_0105",
    "type": "text_list",
    "header": "This Season's Fuel",
    "text": "Who and what is inspiring you the most right now?",
    "subtext": "Note fuel across people, work or projects, health, and anything else.",
    "listLength": 3
   },
   {
    "key": "snapshot_motivationtype",
    "type": "multiple_choice",
    "header": "This Season's Fire",
    "text": "What's lighting the biggest fire under you this season? What's motivating you?",
    "subtext": "Pick the closest one.",
    "options": [
     {
      "text": "Pain or pressure I want out of"
     },
     {
      "text": "Growth I'm hungry for"
     },
     {
      "text": "High performance I'm chasing"
     },
     {
      "text": "A goal or deadline"
     },
     {
      "text": "Someone I'm doing this for"
     },
     {
      "text": "Proving something to myself"
     },
     {
      "text": "Not sure yet"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_motivation",
    "type": "text_or_audio",
    "header": "This Season's Fire",
    "text": "Say more. What's motivating you this season? Why?",
    "subtext": "Pain, growth, performance, a person, a deadline. Whatever is really driving you. Say it plainly."
   },
   {
    "key": "cb_fuel",
    "type": "callback_summary",
    "header": "Fuel this season",
    "text": "Add anything else you feel is fueling you this season.",
    "subtext": "Edit anything that feels off."
   }
  ]
 },
 {
  "number": 3,
  "slug": "life-census",
  "title": "Life Census",
  "part": "now",
  "questions": [
   {
    "key": "lp_where",
    "type": "description",
    "header": "A closer look",
    "text": "Your onboarding already mapped most of your 10 Pillars.\n\nHere we go deeper on the two that anchor the rest: your higher purpose and your spirituality.",
    "subtext": "Mark where you are today, then where you want to be. Go with your gut."
   },
   {
    "key": "snapshot_purposenow",
    "type": "rating",
    "header": "This Season: Higher Purpose",
    "text": "Where are you today with Higher Purpose?",
    "low": "off track",
    "high": "at my best"
   },
   {
    "key": "snapshot_purposesoon",
    "type": "rating",
    "header": "Next Season: Higher Purpose",
    "text": "Where do you want to be with Higher Purpose?",
    "subtext": "In 6 months...",
    "low": "off track",
    "high": "at my best"
   },
   {
    "key": "snapshot_spiritnow",
    "type": "rating",
    "header": "This Season: Spirituality",
    "text": "Where are you today with Spirituality?",
    "low": "off track",
    "high": "at my best"
   },
   {
    "key": "snapshot_spiritsoon",
    "type": "rating",
    "header": "Next Season: Spirituality",
    "text": "Where do you want to be with Spirituality?",
    "subtext": "In 6 months...",
    "low": "off track",
    "high": "at my best"
   },
   {
    "key": "snapshot_gaparea",
    "type": "text_or_audio",
    "header": "Reflect",
    "text": "Thinking about your 10 Pillars and higher purpose, is there an area that excites you most to grow?\n\nOr an area you want relief from pain?",
    "subtext": "Why that area, and what would change if you closed that gap?"
   }
  ]
 },
 {
  "number": 4,
  "slug": "virtues-and-values",
  "title": "Virtues and Values",
  "part": "now",
  "questions": [
   {
    "key": "snapshot_values",
    "type": "multiple_select",
    "header": "My Core Values and Virtues",
    "text": "What are the values and virtues that mean the most to you?",
    "subtext": "Select the ones that mean the most. Aim for at least 6. You can add your own.",
    "options": [
     {
      "text": "Accountability"
     },
     {
      "text": "Authenticity"
     },
     {
      "text": "Courage"
     },
     {
      "text": "Creativity"
     },
     {
      "text": "Curiosity"
     },
     {
      "text": "Empathy"
     },
     {
      "text": "Faith"
     },
     {
      "text": "Gratitude"
     },
     {
      "text": "Growth and learning"
     },
     {
      "text": "Honesty"
     },
     {
      "text": "Kindness"
     },
     {
      "text": "Purpose"
     },
     {
      "text": "Resilience"
     },
     {
      "text": "Wisdom"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_valuesalways",
    "type": "text_list",
    "header": "Values I live fully",
    "text": "Which values do you feel you already live fully, most days?",
    "subtext": "Your selected values and virtues: the values that matter most to you\n\nList the ones you feel you already put into practice most days.",
    "listLength": 3
   },
   {
    "key": "snapshot_valuesnotalways",
    "type": "text_list",
    "header": "Values I live, but not always",
    "text": "Which of your values do you feel like you don't always live by?",
    "subtext": "Your selected values and virtues: the values that matter most to you\n\nList the ones you want to put into practice more consistently.",
    "listLength": 3
   },
   {
    "key": "snapshot_valueswantmore",
    "type": "text_list",
    "header": "Values I want to incorporate more",
    "text": "Which values do you want to incorporate more?",
    "subtext": "Your selected values and virtues: the values that matter most to you\n\nWhich would you like to make more room for in everyday life? You can include another value, too.",
    "listLength": 3
   },
   {
    "key": "cb_values",
    "type": "callback_summary",
    "header": "Values snapshot",
    "text": "Here is what your values point to. Edit anything that feels off.",
    "subtext": "A quick read."
   }
  ]
 },
 {
  "number": 5,
  "slug": "your-story",
  "title": "Your Story",
  "part": "now",
  "questions": [
   {
    "key": "snapshot_greatestimpact",
    "type": "text_list",
    "header": "Biggest Impact",
    "text": "Who has had the greatest impact on your life, and why?",
    "subtext": "For each person, note their name and why they mattered.",
    "listLength": 4
   },
   {
    "key": "snapshot_painfulmoments",
    "type": "text_list",
    "header": "Inflection Points",
    "text": "What have been the biggest moments of pain in your life? How did they make you better?",
    "subtext": "For each, note the pain and the silver lining it revealed.",
    "listLength": 3,
    "labels": [
     "pain: silver lining:"
    ]
   },
   {
    "key": "snapshot_challenges",
    "type": "text_list",
    "header": "Current Pain Points",
    "text": "What top 3 challenges are you currently facing? And how are you seeking to resolve them?",
    "subtext": "For each, note the challenge and your plan.",
    "listLength": 3
   },
   {
    "key": "cb_story",
    "type": "callback_summary",
    "header": "Your story snapshot",
    "text": "Here is what your story is telling us. Edit anything that feels off.",
    "subtext": "A quick reflection."
   }
  ]
 },
 {
  "number": 6,
  "slug": "swot",
  "title": "SWOT",
  "part": "now",
  "questions": [
   {
    "key": "lp_0180",
    "type": "description",
    "header": "SWOT Snapshot",
    "text": "Socrates said the unexamined life is not worth living.\n\nIn examining yours, what are your current strengths, weaknesses, opportunities, and threats?",
    "subtext": "Four quick reads. Add your own with Other."
   },
   {
    "key": "swot_lp",
    "type": "multiple_reveal",
    "header": "",
    "text": "What does SWOT stand for?",
    "subtext": "Tap each to reveal.",
    "options": [
     {
      "text": "S",
      "detail": "Strengths. What you are good at.\n\nExample: I stay calm under pressure."
     },
     {
      "text": "W",
      "detail": "Weaknesses. Where you get in your own way.\n\nExample: I procrastinate when overwhelmed."
     },
     {
      "text": "O",
      "detail": "Opportunities. What is open to you.\n\nExample: a leadership program I could join."
     },
     {
      "text": "T",
      "detail": "Threats. What could pull you off track.\n\nExample: financial stress makes it hard to focus."
     }
    ]
   },
   {
    "key": "snapshot_strengths",
    "type": "multiple_select",
    "header": "Strengths",
    "text": "Strengths: what are you strong at this season?",
    "subtext": "Select what fits.",
    "options": [
     {
      "text": "Discipline"
     },
     {
      "text": "Resilience"
     },
     {
      "text": "Empathy"
     },
     {
      "text": "Communication"
     },
     {
      "text": "Creativity"
     },
     {
      "text": "Consistency"
     },
     {
      "text": "Leadership"
     },
     {
      "text": "Curiosity"
     },
     {
      "text": "Routines"
     },
     {
      "text": "Mindset"
     },
     {
      "text": "Physical health"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_weaknesses",
    "type": "multiple_select",
    "header": "Weaknesses",
    "text": "Weaknesses: where do you tend to get in your own way?",
    "subtext": "Select what fits. This is honesty, not judgment.",
    "options": [
     {
      "text": "Procrastination"
     },
     {
      "text": "Overthinking"
     },
     {
      "text": "Avoidance"
     },
     {
      "text": "Impatience"
     },
     {
      "text": "Self-doubt"
     },
     {
      "text": "Disorganization"
     },
     {
      "text": "People-pleasing"
     },
     {
      "text": "Burning out"
     },
     {
      "text": "Distraction"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_opportunities",
    "type": "multiple_select",
    "header": "Opportunities",
    "text": "Opportunities: what is open to you right now?",
    "subtext": "Select what fits.",
    "options": [
     {
      "text": "A new skill to learn"
     },
     {
      "text": "A relationship to deepen"
     },
     {
      "text": "A role or program to pursue"
     },
     {
      "text": "A habit to build"
     },
     {
      "text": "A community to join"
     },
     {
      "text": "A mentor to find"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_threats",
    "type": "multiple_select",
    "header": "Threats",
    "text": "Threats: what could pull you off track?",
    "subtext": "Select what fits.",
    "options": [
     {
      "text": "Financial stress"
     },
     {
      "text": "Burnout"
     },
     {
      "text": "Unhelpful influences"
     },
     {
      "text": "Screens and distraction"
     },
     {
      "text": "Health strain"
     },
     {
      "text": "Comparison"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "lp_0185",
    "type": "callback_summary",
    "header": "SWOT snapshot",
    "text": "Here is what we captured from your SWOT. Edit anything that feels off.",
    "subtext": "Short and tight."
   },
   {
    "key": "lp_0186",
    "type": "description",
    "header": "Simone Weil",
    "text": "“I am not what happened to me. I am who I choose to become.”"
   }
  ]
 },
 {
  "number": 7,
  "slug": "flourishing",
  "title": "Flourishing",
  "part": "vision",
  "questions": [
   {
    "key": "lp_0201",
    "type": "description",
    "header": "Kenichi Ohmae",
    "text": "“Rowing harder does not help if the boat is headed in the wrong direction.”"
   },
   {
    "key": "lp_0202",
    "type": "description",
    "header": "Where do I want to go?",
    "text": "This section is about direction. Purpose, vision, and the life you are actually rowing toward."
   },
   {
    "key": "sci_flourish",
    "type": "boolean",
    "header": "The Science",
    "text": "Research suggests that having a clear vision reduces decision fatigue.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "When your values and goals are clearly defined, fewer daily decisions require internal debate. Clarity lowers cognitive load and preserves mental energy for what matters. Vohs, Baumeister, Schmeichel et al. (2008), Journal of Personality and Social Psychology.",
    "correct": true
   },
   {
    "key": "snapshot_flourishing",
    "type": "text_list",
    "header": "Flourishing",
    "text": "What does your life look like when you are thriving? Not perfect, but at your best.",
    "subtext": "Picture it across each area of your life. Fill in what fits.",
    "listLength": 7,
    "labels": [
     "Physically:",
     "Emotionally and mentally:",
     "Professionally:",
     "Relationally and socially:",
     "Recreationally and personally:",
     "Financially:",
     "Spiritually and purposefully:"
    ]
   }
  ]
 },
 {
  "number": 8,
  "slug": "vision-and-mission",
  "title": "Vision and Mission",
  "part": "vision",
  "questions": [
   {
    "key": "vm_intro",
    "type": "description",
    "header": "Warm Up",
    "text": "Let's build your vision and mission without overthinking it.",
    "subtext": "Short, honest, practical. We'll go step by step to draft it with you."
   },
   {
    "key": "vm_space",
    "type": "multiple_select",
    "header": "",
    "text": "What kind of space helps you feel most like yourself?",
    "subtext": "Select up to two.",
    "options": [
     {
      "text": "In nature"
     },
     {
      "text": "Around people I love"
     },
     {
      "text": "In quiet and solitude"
     },
     {
      "text": "In motion or exercise"
     },
     {
      "text": "Creating something"
     },
     {
      "text": "Other",
      "other": true
     }
    ],
    "maxSelections": 2
   },
   {
    "key": "vm_when",
    "type": "text_or_audio",
    "header": "",
    "text": "Finish the sentence: I feel most like myself when I'm..."
   },
   {
    "key": "vm_words",
    "type": "text_list",
    "header": "",
    "text": "What are three words that describe your best self?",
    "listLength": 3
   },
   {
    "key": "vm_vstyle",
    "type": "multiple_choice",
    "header": "",
    "text": "Which \"style\" of vision statement feels most like you right now?",
    "subtext": "You can change it later too.",
    "options": [
     {
      "text": "Bold and ambitious"
     },
     {
      "text": "Calm and grounded"
     },
     {
      "text": "Service and impact"
     },
     {
      "text": "Freedom and adventure"
     },
     {
      "text": "Steady and secure"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "vm_mstart",
    "type": "multiple_choice",
    "header": "",
    "text": "Pick a mission sentence starter you like.",
    "subtext": "This is just a scaffold.",
    "options": [
     {
      "text": "My mission is to..."
     },
     {
      "text": "I want to help ___ by ___"
     },
     {
      "text": "I'm here to..."
     },
     {
      "text": "I want to be known for..."
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "lp_0205",
    "type": "text_or_audio",
    "header": "Vision",
    "text": "In 20 years, what kind of life do you want to be leading, and what impact do you want to have?",
    "subtext": "Values you chose: the values that matter most to you\n\nTry starting with: Twenty years from now, I want to be able to say I..."
   },
   {
    "key": "lp_0206",
    "type": "text_or_audio",
    "header": "Mission",
    "text": "What are you most focused on right now? What feels like your current mission?",
    "subtext": "What you said about your future: the future you want to work toward\n\nIn one sentence, describe what matters most to work on now, who or what it serves, and how you want to show up."
   },
   {
    "key": "cb_vision_draft",
    "type": "callback_summary",
    "header": "Your vision, starting point",
    "text": "Here is a vision draft compiled from your words. Edit until it feels true."
   },
   {
    "key": "cb_mission_draft",
    "type": "callback_summary",
    "header": "Your mission, starting point",
    "text": "Here is a mission draft compiled from your words. Edit until it feels true."
   },
   {
    "key": "cb_motto",
    "type": "callback_summary",
    "header": "Motto",
    "text": "What phrase could be this season's theme?",
    "subtext": "Edit until it sounds like you."
   },
   {
    "key": "vm_feedback",
    "type": "multiple_choice",
    "header": "Quick Feedback",
    "text": "Quick check: was this section clear and easy to follow?",
    "options": [
     {
      "text": "Very clear"
     },
     {
      "text": "Mostly clear"
     },
     {
      "text": "Somewhat confusing"
     },
     {
      "text": "Very confusing"
     }
    ]
   },
   {
    "key": "cb_vm_snapshot",
    "type": "callback_summary",
    "header": "Section Snapshot",
    "text": "Zooming out, here is how you have sounded so far.",
    "subtext": "A quick recap you can edit."
   }
  ]
 },
 {
  "number": 9,
  "slug": "defining-success",
  "title": "Defining Success",
  "part": "vision",
  "questions": [
   {
    "key": "success_quote",
    "type": "description",
    "header": "John Wooden on Success",
    "text": "“Success is peace of mind that comes from knowing you did your best to become the best you are capable of becoming.”",
    "subtext": "Whether or not that is exactly right for you, it's added perspective."
   },
   {
    "key": "success_initial",
    "type": "text_or_audio",
    "header": "Defining Success",
    "text": "Before we go further, what does success mean to you right now?",
    "subtext": "Your first take, in your own words. We will come back to it."
   },
   {
    "key": "success_control",
    "type": "multiple_reveal",
    "header": "What's In Your Hands",
    "text": "Where you place success changes your peace of mind. Some things are yours to control. Some are not.",
    "subtext": "Tap each to explore.",
    "options": [
     {
      "text": "In your control",
      "detail": "Your effort, your attitude, your preparation, how you treat people, how you respond when things go sideways."
     },
     {
      "text": "Out of your control",
      "detail": "The final result, other people's opinions, how you compare, timing, and luck."
     }
    ]
   },
   {
    "key": "sci_control",
    "type": "boolean",
    "header": "The Science",
    "text": "Tying success to your effort and attitude, not just outcomes, protects your motivation and peace of mind.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "When self-worth rides only on results, motivation and wellbeing drop the moment outcomes slip outside our control. Anchoring on effort and process is more durable. Dweck (2006), Mindset; Nicholls (1984) on task versus ego orientation.",
    "correct": true
   },
   {
    "key": "success_sort",
    "type": "horizontal_drag_drop",
    "header": "",
    "text": "Sort each factor by whether it is yours to control.",
    "subtext": "Drag each item into the column that fits. You can continue once all are placed.",
    "low": "In my control",
    "high": "Out of my control",
    "options": [
     {
      "text": "My effort and preparation",
      "bucket": "left"
     },
     {
      "text": "My attitude",
      "bucket": "left"
     },
     {
      "text": "How I treat people",
      "bucket": "left"
     },
     {
      "text": "How I respond when things go wrong",
      "bucket": "left"
     },
     {
      "text": "The final result",
      "bucket": "right"
     },
     {
      "text": "What other people think",
      "bucket": "right"
     },
     {
      "text": "How I stack up to others in any given category",
      "bucket": "right"
     },
     {
      "text": "Timing and luck",
      "bucket": "right"
     },
     {
      "text": "Choosing whether or not to compare myself to others",
      "bucket": "left"
     }
    ]
   },
   {
    "key": "success_refined",
    "type": "callback_summary",
    "header": "Success, redefined",
    "text": "What would it sound like if your definition of success were framed more around what's in your control?",
    "subtext": "Edit until it feels like yours."
   }
  ]
 },
 {
  "number": 10,
  "slug": "perspective",
  "title": "Perspective",
  "part": "vision",
  "questions": [
   {
    "key": "lp_0207",
    "type": "description",
    "header": "10 Breath Meditation",
    "text": "Take a brain break.\n\n1. Sit or stand comfortably and close your eyes.\n2. Count your breaths up to 10, then back down to 1.\n3. Your mind will drift. That is normal. Notice it, return to your breath, and continue.",
    "subtext": "When you reach 1, open your eyes and notice the space around you."
   },
   {
    "key": "lp_trigger",
    "type": "description",
    "header": "Disclaimer",
    "text": "This section contains sensitive content around end of life experiences.",
    "subtext": "If you would like to take a pause and return to it at a later time, please. Otherwise, continue."
   },
   {
    "key": "snapshot_deathbedthank",
    "type": "text_or_audio",
    "header": "Deathbed Perspective | Thank",
    "text": "If you were in your last week of life, what would you need to get off your chest?\n\nWho would you thank, and why?",
    "subtext": "No right answers."
   },
   {
    "key": "snapshot_deathbedforgive",
    "type": "text_or_audio",
    "header": "Deathbed Perspective | Forgive & Apologize",
    "text": "If you were in your last week of life, who would you forgive, or apologize to, and why?",
    "subtext": "No right answers."
   },
   {
    "key": "snapshot_deathbedmoreenergy",
    "type": "text_list",
    "header": "More energy",
    "text": "Considering that perspective, what deserves more of your energy?",
    "subtext": "List what you want to give more of your life to (values, actions, habits...)",
    "listLength": 3
   },
   {
    "key": "snapshot_deathbedlessenergy",
    "type": "text_list",
    "header": "Less energy",
    "text": "And what deserves less of your energy?",
    "subtext": "List what you want to give less of your life to.",
    "listLength": 3
   },
   {
    "key": "lp_0212",
    "type": "callback_summary",
    "header": "Your direction",
    "text": "Based on what you said, here is the direction you seem to be pointing...",
    "subtext": "Edit until it feels true to you."
   },
   {
    "key": "lp_0213",
    "type": "description",
    "header": "Ralph Waldo Emerson",
    "text": "“The only person you are destined to become is the person you decide to be.”"
   }
  ]
 },
 {
  "number": 11,
  "slug": "fundamentals",
  "title": "Fundamentals",
  "part": "path",
  "questions": [
   {
    "key": "lp_0301",
    "type": "description",
    "header": "Epictetus",
    "text": "“First say to yourself what you would be, and then do what you have to do.”"
   },
   {
    "key": "lp_0302",
    "type": "description",
    "header": "Fundamentals",
    "text": "As we build towards a flourishing life, we must first notice how we get in our own way."
   },
   {
    "key": "lp_obstacles",
    "type": "description",
    "header": "Self-Inflicted Obstacles",
    "text": "Most of us are just trying to ease stress and find peace. Over time we build coping habits, some helpful, some harmful, often without noticing."
   },
   {
    "key": "lp_0303",
    "type": "multiple_reveal",
    "header": "",
    "text": "Coping habits fall on a spectrum. Some restore you. Some only bring temporary relief.",
    "subtext": "Tap each to see examples.",
    "options": [
     {
      "text": "More temporary",
      "detail": "MORE TEMPORARY:\n• Doomscrolling\n• online shopping\n• sugar or caffeine jolts\n• overworking\n• numbing\n• procrastination\n• isolation\n• gaming to escape"
     },
     {
      "text": "More restorative",
      "detail": "MORE RESTORATIVE\n• Breath work\n• hot or cold exposure\n• a mindful walk\n• journaling\n• a mentor call\n• prayer\n• time in nature\n• sleep and rest"
     }
    ]
   },
   {
    "key": "sci_affect",
    "type": "boolean",
    "header": "The Science",
    "text": "Putting a feeling into words can lower how intense it feels.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "Naming an emotion engages the prefrontal cortex and dampens the amygdala's alarm. Researchers call it affect labeling. Lieberman et al. (2007), Psychological Science.",
    "correct": true
   }
  ]
 },
 {
  "number": 12,
  "slug": "coping-and-basics",
  "title": "Coping and Basics",
  "part": "path",
  "questions": [
   {
    "key": "lp_0304",
    "type": "horizontal_drag_drop",
    "header": "",
    "text": "Sort each coping habit toward the effect it usually has.",
    "subtext": "Drag each item into the column that fits. You can continue once all are placed.",
    "low": "More temporary",
    "high": "More restorative",
    "options": [
     {
      "text": "Doomscrolling and news binges",
      "bucket": "left"
     },
     {
      "text": "Overworking or just one more email",
      "bucket": "left"
     },
     {
      "text": "Online shopping carts",
      "bucket": "left"
     },
     {
      "text": "Numbing to avoid a feeling",
      "bucket": "left"
     },
     {
      "text": "Meditation and breath work",
      "bucket": "right"
     },
     {
      "text": "A mindful walk or stretching",
      "bucket": "right"
     },
     {
      "text": "Journaling or letter-writing",
      "bucket": "right"
     },
     {
      "text": "A meaningful talk with a friend",
      "bucket": "right"
     }
    ]
   },
   {
    "key": "lp_0305",
    "type": "multiple_select",
    "header": "Back to Basics",
    "text": "Which coping habits do you turn to most?",
    "subtext": "Be honest. Select the ones you actually reach for.",
    "options": [
     {
      "text": "Doomscrolling"
     },
     {
      "text": "Online shopping"
     },
     {
      "text": "Sugar or caffeine"
     },
     {
      "text": "Overworking"
     },
     {
      "text": "Substances"
     },
     {
      "text": "Procrastination"
     },
     {
      "text": "Isolation"
     },
     {
      "text": "Gaming or streaming"
     },
     {
      "text": "Breath work"
     },
     {
      "text": "A walk or movement"
     },
     {
      "text": "Journaling"
     },
     {
      "text": "Talking to someone"
     },
     {
      "text": "Prayer or quiet"
     },
     {
      "text": "Rest and sleep"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "lp_0306",
    "type": "callback_summary",
    "header": "Ten years from now",
    "text": "If you practiced these daily for 10 years, where would they take you?",
    "subtext": "A reflection you can edit."
   }
  ]
 },
 {
  "number": 13,
  "slug": "dbt-foundations",
  "title": "DBT Foundations",
  "part": "path",
  "questions": [
   {
    "key": "lp_0320",
    "type": "description",
    "header": "Systems Check",
    "text": "It is one thing to know a habit is not helping. It is another to change it.\n\nAt the Inspiring Children Foundation (ICF), a youth development organization, youth practice evidence-based tools like DBT to meet challenges with clearer minds and wiser choices. ICF is included here as a real-world example of putting these tools into practice."
   },
   {
    "key": "lp_0321",
    "type": "multiple_reveal",
    "header": "DBT 101",
    "text": "Dialectical Behavior Therapy is grounded in three timeless truths.",
    "subtext": "Tap each to explore.",
    "options": [
     {
      "text": "Dialectics",
      "detail": "Two opposing things can be true. We can accept ourselves and change."
     },
     {
      "text": "Interconnectedness",
      "detail": "Our emotions, actions, and relationships all affect one another."
     },
     {
      "text": "Impermanence",
      "detail": "Everything changes. No feeling or struggle lasts forever."
     }
    ]
   },
   {
    "key": "dbt_which",
    "type": "multiple_choice",
    "header": "",
    "text": "Which of the three truths do you most need to remember this season?",
    "subtext": "Pick one to carry with you.",
    "options": [
     {
      "text": "Two things can be true"
     },
     {
      "text": "Everything is connected"
     },
     {
      "text": "Nothing lasts forever"
     }
    ]
   }
  ]
 },
 {
  "number": 14,
  "slug": "opposite-action",
  "title": "Opposite Action",
  "part": "path",
  "questions": [
   {
    "key": "lp_0322",
    "type": "multiple_reveal",
    "header": "From Coping to Choosing",
    "text": "One DBT skill, Opposite Action, helps you do the harder, wiser thing when an urge would make things worse.",
    "subtext": "Tap each emotion to see the urge and the opposite action.",
    "options": [
     {
      "text": "Fear",
      "detail": "Urge: avoid.\n\nOpposite: go toward it, build courage."
     },
     {
      "text": "Anger",
      "detail": "Urge: attack or argue.\n\nOpposite: show kindness, or walk away."
     },
     {
      "text": "Sadness",
      "detail": "Urge: withdraw.\n\nOpposite: call a friend."
     },
     {
      "text": "Shame",
      "detail": "Urge: hide.\n\nOpposite: be vulnerable, practice confidence."
     },
     {
      "text": "Guilt",
      "detail": "Urge: avoid responsibility.\n\nOpposite: apologize, make it right."
     }
    ]
   },
   {
    "key": "lp_0323",
    "type": "text_or_audio",
    "header": "My Opposite Action List",
    "text": "What are 2 or 3 moments when your emotions pulled you in a direction that did not serve you?",
    "subtext": "For each:\n• What was the emotion?\n• What was the urge?\n• What opposite action could you choose next time?"
   },
   {
    "key": "cb_opposite",
    "type": "callback_summary",
    "header": "Opposite action snapshot",
    "text": "Here is what your opposite actions could look like.",
    "subtext": "Edit anything that feels off."
   }
  ]
 },
 {
  "number": 15,
  "slug": "triggers-and-small-victories",
  "title": "Triggers and Small Victories",
  "part": "path",
  "questions": [
   {
    "key": "lp_0325",
    "type": "description",
    "header": "Small Victories",
    "text": "Validating an emotion is not approval. It is recognizing the feeling is normal, and giving it grace to exist.\n\nThe challenge: when triggers arise, can we pause long enough to notice, name, and choose a wiser response?"
   },
   {
    "key": "lp_0326",
    "type": "text_or_audio",
    "header": "List of Triggers",
    "text": "What are two recent moments when you felt stressed or reactive?",
    "subtext": "For each:\n• What was the trigger?\n• What emotion showed up?\n• Why might it make sense? (validation)\n• What wiser action could you choose next time?\n\nEveryday examples are enough. Share only what feels useful."
   },
   {
    "key": "cb_triggers",
    "type": "callback_summary",
    "header": "Triggers snapshot",
    "text": "Here is what your triggers are showing you.",
    "subtext": "Edit anything that feels off."
   }
  ]
 },
 {
  "number": 16,
  "slug": "mind-lies-and-mental-loop",
  "title": "Mind Lies and Mental Loop",
  "part": "path",
  "questions": [
   {
    "key": "lp_0327",
    "type": "description",
    "header": "Your Brain is Lying",
    "text": "Some of our biggest stress comes from believing our own false thoughts.\n\nThey feel true in the moment, but the primitive mind distorts partial truths into conclusions that pull us from reality."
   },
   {
    "key": "sci_negbias",
    "type": "boolean",
    "header": "The Science",
    "text": "Our brains tend to weigh bad experiences more heavily than good ones.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "This negativity bias helped our ancestors survive, but today it inflates threats and shrinks wins. Noticing it is the first step to balancing it. Baumeister et al. (2001), Review of General Psychology.",
    "correct": true
   },
   {
    "key": "lp_0328",
    "type": "multiple_reveal",
    "header": "How to Break the Mental Loop",
    "text": "When a stressful thought loops, you can break it down step by step.",
    "subtext": "Tap each step to explore.",
    "options": [
     {
      "text": "1. Write the thought",
      "detail": "What is the recurring belief or story causing you stress?"
     },
     {
      "text": "2. List the evidence",
      "detail": "What facts make this thought feel true?"
     },
     {
      "text": "3. Notice the assumptions",
      "detail": "What conclusions or fears are you adding that are not proven?"
     },
     {
      "text": "4. Challenge them",
      "detail": "What principle or higher truth might make it false or incomplete?"
     },
     {
      "text": "5. Tap your wiser mind",
      "detail": "Give yourself the advice you would offer a friend."
     },
     {
      "text": "6. Reframe the truth",
      "detail": "Restate the thought in a more balanced, realistic way."
     }
    ]
   },
   {
    "key": "lies_examples",
    "type": "multiple_reveal",
    "header": "List of Lies: Examples",
    "text": "Here is what walking a thought through the loop can look like.",
    "subtext": "Tap each to see a worked example.",
    "options": [
     {
      "text": "I'm falling behind",
      "detail": "Evidence: people around me are advancing.\n\nAssumptions: if my progress looks different, I'm behind.\n\nChallenge: what if growth isn't the same for everyone?\n\nWiser truth: if I give my best and drop the comparison, I'm at the right pace for me."
     },
     {
      "text": "If I don't push harder, I'll lose it all",
      "detail": "Evidence: I worked hard to get here.\n\nAssumptions: if I rest, it falls apart.\n\nChallenge: what if rest is part of progress?\n\nWiser truth: I can work hard and still rest. Who I am is not what I have."
     }
    ]
   },
   {
    "key": "lp_0329",
    "type": "text_list",
    "header": "My List of Lies",
    "text": "Pick one recurring thought that causes you stress, and investigate it.",
    "subtext": "Fill in each line to walk one recurring thought through the loop.",
    "listLength": 6,
    "labels": [
     "Thought:",
     "Evidence:",
     "Assumptions:",
     "Challenge:",
     "Principles:",
     "Wiser truth:"
    ]
   },
   {
    "key": "cb_lies",
    "type": "callback_summary",
    "header": "Reframe snapshot",
    "text": "Here is the wiser truth underneath the loop.",
    "subtext": "Edit anything that feels off."
   }
  ]
 },
 {
  "number": 17,
  "slug": "wheel-of-influence",
  "title": "Wheel of Influence",
  "part": "path",
  "questions": [
   {
    "key": "lp_0340",
    "type": "description",
    "header": "Epictetus",
    "text": "“You become what you give your attention to.”"
   },
   {
    "key": "snapshot_topinfluences",
    "type": "text_list",
    "header": "People I'm Influenced By",
    "text": "Who are the top influences in your life today?",
    "subtext": "List them. Then notice: who helps you move toward your vision, and who may pull you off track?",
    "listLength": 6
   },
   {
    "key": "influence_shift",
    "type": "multiple_choice",
    "header": "",
    "text": "What small shift would make your circle more supportive?",
    "subtext": "Pick the one most worth trying.",
    "options": [
     {
      "text": "More time with someone who lifts me"
     },
     {
      "text": "Less time with someone who drains me"
     },
     {
      "text": "A new mentor or guide"
     },
     {
      "text": "A hard but honest conversation"
     },
     {
      "text": "More community"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "cb_influence",
    "type": "callback_summary",
    "header": "Influence snapshot",
    "text": "Here is what your circle is telling you.",
    "subtext": "Edit anything that feels off."
   }
  ]
 },
 {
  "number": 18,
  "slug": "daily-routines",
  "title": "Daily Routines",
  "part": "path",
  "questions": [
   {
    "key": "lp_0342",
    "type": "description",
    "header": "Jim Rohn",
    "text": "“Success is nothing more than a few simple disciplines, practiced every day.”"
   },
   {
    "key": "morining_habits",
    "type": "multiple_select",
    "header": "Daily Routines: Morning",
    "text": "Which morning habits resonate with you?",
    "subtext": "Select any you would like to build in.",
    "options": [
     {
      "text": "Walk across the room to turn off the alarm"
     },
     {
      "text": "Tall glass of water with a pinch of salt"
     },
     {
      "text": "Light stretching or yoga"
     },
     {
      "text": "Meditation"
     },
     {
      "text": "Exercise"
     },
     {
      "text": "Hot or cold shower"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "daytime_habits",
    "type": "multiple_select",
    "header": "Daily Routines: Daytime",
    "text": "Which daytime habits resonate with you?",
    "subtext": "Select any you would like to build in.",
    "options": [
     {
      "text": "10-breath meditations between calls"
     },
     {
      "text": "Brain breaks"
     },
     {
      "text": "Mindful eating"
     },
     {
      "text": "Short walks"
     },
     {
      "text": "Walk-and-talk catch-ups"
     },
     {
      "text": "Small exercise breaks"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "evening_habits",
    "type": "multiple_select",
    "header": "Daily Routines: Evening",
    "text": "Which evening habits resonate with you?",
    "subtext": "Select any you would like to build in.",
    "options": [
     {
      "text": "No screens 60 minutes before bed"
     },
     {
      "text": "Blue-light glasses"
     },
     {
      "text": "Warm shower"
     },
     {
      "text": "Light stretching"
     },
     {
      "text": "Gratitude or prayer"
     },
     {
      "text": "Journal"
     },
     {
      "text": "Alarm set and phone across the room"
     },
     {
      "text": "4-7-8 breathing"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "daily_routine",
    "type": "text_or_audio",
    "header": "My Daily Routines",
    "text": "What small habits could help you feel and function well while moving toward the life you want?",
    "subtext": "The area you wanted to grow in: an area of life you would like to grow in\n\nValues you wanted to make more room for: the values you want to practice\n\nSketch a morning, daytime, and evening routine that feels realistic. Small changes count."
   }
  ]
 },
 {
  "number": 19,
  "slug": "reset-and-environment",
  "title": "Reset and Environment",
  "part": "path",
  "questions": [
   {
    "key": "rest_priming",
    "type": "description",
    "header": "Military Grade Rest",
    "text": "Do you give recovery the same attention as performance?\n\nThe most productive thing you can do is often rest.\n\nRecovery is not the opposite of the work. It is part of it.",
    "subtext": "— Ryan Wolfington"
   },
   {
    "key": "reset_tiers",
    "type": "multiple_reveal",
    "header": "The Reset Menu",
    "text": "Resets come in three tiers. You need all three, not just the big ones.",
    "subtext": "Tap each tier to explore.",
    "options": [
     {
      "text": "Foundational resets",
      "detail": "• Deep sleep and naps\n• big nature\n• yoga or stretching\n• hot or cold immersion\n• breath work\n• whole foods and hydration\n• screen fasting\n\nThe base layer."
     },
     {
      "text": "Powerful boosts",
      "detail": "• Intense movement\n• sensory float\n• social support and laughter\n• creative expression\n• water therapy\n• mindful walking\n• prayer or ritual\n• recreational play\n\nBigger swings."
     },
     {
      "text": "Everyday restorers",
      "detail": "• Mindful routines\n• art and entertainment\n• gentle movement\n• time with animals and nature\n\nSmall and repeatable."
     }
    ]
   },
   {
    "key": "sci_rest",
    "type": "boolean",
    "header": "The Science",
    "text": "Rest and sleep are when the body repairs and locks in what you learned.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "During deep sleep the brain clears waste and consolidates memory, which is why recovery drives performance as much as the effort itself. Walker (2017), Why We Sleep.",
    "correct": true
   },
   {
    "key": "lp_0348",
    "type": "multiple_select",
    "header": "Reset Menu",
    "text": "Which reset practices do you already use to restore your nervous system?",
    "subtext": "Select all you already lean on.",
    "options": [
     {
      "text": "Deep sleep and naps"
     },
     {
      "text": "Big nature"
     },
     {
      "text": "Massage"
     },
     {
      "text": "Yoga or stretching"
     },
     {
      "text": "Hot or cold immersion"
     },
     {
      "text": "Breath work"
     },
     {
      "text": "Whole foods and hydration"
     },
     {
      "text": "Screen fasting"
     },
     {
      "text": "Intense movement"
     },
     {
      "text": "Social support and laughter"
     },
     {
      "text": "Creative expression"
     },
     {
      "text": "Water therapy"
     },
     {
      "text": "Gratitude journaling"
     },
     {
      "text": "Mindful walking"
     },
     {
      "text": "Prayer or ritual"
     }
    ]
   },
   {
    "key": "lp_0349",
    "type": "multiple_select",
    "header": "Reset Menu",
    "text": "Which reset practices would you like to try?",
    "subtext": "Select the ones worth experimenting with.",
    "options": [
     {
      "text": "Deep sleep and naps"
     },
     {
      "text": "Big nature"
     },
     {
      "text": "Massage"
     },
     {
      "text": "Yoga or stretching"
     },
     {
      "text": "Hot or cold immersion"
     },
     {
      "text": "Breath work"
     },
     {
      "text": "Whole foods and hydration"
     },
     {
      "text": "Screen fasting"
     },
     {
      "text": "Intense movement"
     },
     {
      "text": "Social support and laughter"
     },
     {
      "text": "Creative expression"
     },
     {
      "text": "Water therapy"
     },
     {
      "text": "Gratitude journaling"
     },
     {
      "text": "Mindful walking"
     },
     {
      "text": "Prayer or ritual"
     }
    ]
   },
   {
    "key": "lp_0350",
    "type": "text_list",
    "header": "Military Grade Rest",
    "text": "Do you give recovery the same attention as performance? What are your top go-to resets?",
    "subtext": "List the resets you reach for when you need to reset your nervous system.",
    "listLength": 6
   },
   {
    "key": "lp_0351",
    "type": "multiple_select",
    "header": "Environmental Shift Menu",
    "text": "How can you set up your space so healthy habits come more naturally?",
    "subtext": "Select the shifts you would like to make.",
    "options": [
     {
      "text": "Phone outside the bedroom"
     },
     {
      "text": "Alarm across the room"
     },
     {
      "text": "Blackout curtains"
     },
     {
      "text": "Blue-light glasses"
     },
     {
      "text": "Meal prep"
     },
     {
      "text": "Water bottle nearby"
     },
     {
      "text": "Workout shoes by the door"
     },
     {
      "text": "Gym bag packed"
     },
     {
      "text": "Journal visible"
     },
     {
      "text": "Inspirational quotes on screen"
     },
     {
      "text": "App limits"
     },
     {
      "text": "Greyscale phone"
     },
     {
      "text": "Focus timers"
     },
     {
      "text": "Screen curfew"
     },
     {
      "text": "Unsubscribe or remove apps"
     }
    ]
   },
   {
    "key": "environmental_nudge",
    "type": "text_or_audio",
    "header": "Environmental Nudges",
    "text": "What are a few environmental nudges you can set up at home?",
    "subtext": "The routine you described: a daily routine you would like to support\n\nChoose a small change to your space that would make a helpful habit easier or an unhelpful habit less automatic. For each, name the struggle and the change.\n\nExample: I reach for my phone at bedtime, so I will put a book on my nightstand and charge my phone across the room."
   }
  ]
 },
 {
  "number": 20,
  "slug": "next-right-things",
  "title": "Next Right Things",
  "part": "path",
  "questions": [
   {
    "key": "lp_0375",
    "type": "description",
    "header": "Aristotle",
    "text": "“We are what we repeatedly do. Excellence, then, is not an act, but a habit.”"
   },
   {
    "key": "snapshot_stop",
    "type": "text_list",
    "header": "Where I Can Change: Stop",
    "text": "What actions do you know you should stop to move toward where you want to go?",
    "subtext": "List what you will stop.",
    "listLength": 4
   },
   {
    "key": "snapshot_continue",
    "type": "text_list",
    "header": "Where I Can Change: Continue",
    "text": "What actions do you know you should continue?",
    "subtext": "List what is working, that you will keep.",
    "listLength": 4
   },
   {
    "key": "snapshot_start",
    "type": "text_list",
    "header": "Where I Can Change: Start",
    "text": "What actions do you know you should start?",
    "subtext": "List what you will begin.",
    "listLength": 4
   },
   {
    "key": "sci_impint",
    "type": "boolean",
    "header": "The Science",
    "text": "People follow through more when they decide in advance when and where they will act.",
    "subtext": "True or false?",
    "low": "FALSE",
    "high": "TRUE",
    "options": [
     {
      "text": "TRUE"
     },
     {
      "text": "FALSE"
     }
    ],
    "explanation": "Deciding the exact cue, when and where, not just the goal, sharply raises follow-through. Researchers call these implementation intentions. Gollwitzer (1999), American Psychologist.",
    "correct": true
   },
   {
    "key": "snapshot_toppriorities",
    "type": "text_list",
    "header": "My Life Plan Next Steps",
    "text": "Based on all your reflections, what are your top 3 priorities this season (aka your next right things)?",
    "subtext": "Your vision, in your own words: the future you want to work toward\n\nYour mission, in your own words: what matters most to you right now\n\nFor each priority, note:\n• The next right thing\n• Who or what could support you\n• Your first step this week",
    "listLength": 3
   },
   {
    "key": "lp_0376",
    "type": "description",
    "header": "James Clear",
    "text": "“You do not rise to the level of your goals. You fall to the level of your systems.”"
   },
   {
    "key": "lp_0377",
    "type": "multiple_reveal",
    "header": "Make It a Habit",
    "text": "To turn a next right thing into a habit, build a small system around it.",
    "subtext": "Tap each step to explore.",
    "options": [
     {
      "text": "1. Name your cue",
      "detail": "When and where does this happen?\n\nAttach it to a time, a place, something you already do, even a feeling or urge."
     },
     {
      "text": "2. Write your if-then plan",
      "detail": "Write the sentence: If [cue], then I will [next right thing].\n\nThis helps your plan become more automatic."
     },
     {
      "text": "3. Give yourself a reward",
      "detail": "A small way to celebrate the win: tell a friend, a fun snack, a check on a tracker, a self-high-five."
     },
     {
      "text": "4. Notice what's different",
      "detail": "What can you point to that changed?\n\nFor example: less exhausted waking up, that conversation didn't spiral, more patient with my mom."
     }
    ]
   },
   {
    "key": "lp_0378",
    "type": "multiple_reveal",
    "header": "Making It a Habit: Examples",
    "text": "Here is what a finished habit plan can look like.",
    "subtext": "Tap each to see a worked example.",
    "options": [
     {
      "text": "Sleep more",
      "detail": "If it's 10pm on a weekday, I plug my phone into the kitchen for the night.\n\nReward: two pages of my book before lights off.\n\nDifferent: less sluggish at 2pm, more patient with others."
     },
     {
      "text": "More patient with people",
      "detail": "If I notice I'm building a case for why they're wrong, I take three deep breaths before speaking.\n\nReward: text my friend that I did it.\n\nDifferent: the conversation didn't spiral."
     }
    ]
   },
   {
    "key": "snapshot_habit1",
    "type": "text_list",
    "header": "My Habit Plan: Goal 1",
    "text": "Build the habit around your first next right thing.",
    "subtext": "Turn this priority into a plan you can use:\n• The goal: what you want to do\n• What done looks like: how you will recognize progress or completion\n• By when or how often: your timing or frequency\n• The cue: when or where you will begin\n• The reward: a small way to acknowledge following through\n• First step this week: one action to get started",
    "listLength": 6
   },
   {
    "key": "snapshot_habit2",
    "type": "text_list",
    "header": "My Habit Plan: Goal 2",
    "text": "Build the habit around your second next right thing.",
    "subtext": "Turn this priority into a plan you can use:\n• The goal: what you want to do\n• What done looks like: how you will recognize progress or completion\n• By when or how often: your timing or frequency\n• The cue: when or where you will begin\n• The reward: a small way to acknowledge following through\n• First step this week: one action to get started",
    "listLength": 6
   },
   {
    "key": "snapshot_habit3",
    "type": "text_list",
    "header": "My Habit Plan: Goal 3",
    "text": "Build the habit around your third next right thing.",
    "subtext": "Turn this priority into a plan you can use:\n• The goal: what you want to do\n• What done looks like: how you will recognize progress or completion\n• By when or how often: your timing or frequency\n• The cue: when or where you will begin\n• The reward: a small way to acknowledge following through\n• First step this week: one action to get started",
    "listLength": 6
   },
   {
    "key": "lp_0381",
    "type": "multiple_select",
    "header": "Who's Holding You Accountable: The Ask",
    "text": "What kind of support would help you follow through?",
    "subtext": "Choose the asks that would actually help you follow through.",
    "options": [
     {
      "text": "Text me to ask how it's going"
     },
     {
      "text": "Bring it up each time we talk"
     },
     {
      "text": "Look at my tracker"
     },
     {
      "text": "Weekly check-in"
     },
     {
      "text": "Other",
      "other": true
     }
    ]
   },
   {
    "key": "snapshot_acocuntabilitypartner",
    "type": "text_or_audio",
    "header": "Your Accountability Partner",
    "text": "Who is holding you accountable, and if you stray, what should they say?",
    "subtext": "Name the person, and the words you would want to hear if you drift off track."
   },
   {
    "key": "lp_0383",
    "type": "description",
    "header": "Calibration",
    "text": "Set a calendar reminder for 90 days from now to revisit your Next Right Things and reflect on:\n• What worked\n• What didn't\n• What now",
    "subtext": "Future you will be glad you scheduled this."
   },
   {
    "key": "lp_declaration",
    "type": "callback_summary",
    "header": "Life Plan Declaration",
    "text": "Your Life Plan Declaration, drafted from everything you entered.",
    "subtext": "Edit until it sounds like you."
   },
   {
    "key": "nrt_feedback",
    "type": "multiple_choice",
    "header": "Quick Feedback",
    "text": "Quick check: was this section clear and easy to follow?",
    "options": [
     {
      "text": "Very clear"
     },
     {
      "text": "Mostly clear"
     },
     {
      "text": "Somewhat confusing"
     },
     {
      "text": "Very confusing"
     }
    ]
   },
   {
    "key": "lp_0384",
    "type": "callback_summary",
    "header": "Your Life Plan snapshot",
    "text": "Here is your Life Plan, in your own words.",
    "subtext": "A summary you can keep and edit."
   },
   {
    "key": "lp_0401",
    "type": "description",
    "header": "More to Come",
    "text": "What you have just completed is one chapter of a much larger vision.\n\nFor over 25 years, the Inspiring Children Foundation has developed a curriculum of healing, growth, and high-performance living, with over 40 tools and 100 activations."
   },
   {
    "key": "lp_0402",
    "type": "description",
    "header": "Let's Cause a Ripple",
    "text": "If this work resonates, there are ways to get involved:\n• Bring it into your community\n• Join future pilots\n• Share your expertise\n• Join the waitlist for more chapters\n\nReach out to info@wisefriend.org."
   },
   {
    "key": "lp_0403",
    "type": "description",
    "header": "Jane Goodall",
    "text": "“What you do makes a difference, and you have to decide what kind of difference you want to make.”"
   }
  ]
 }
];

export const LIFE_PLAN_QUESTION_COUNT = 131;
